import type { FC } from 'react';
import React, { useEffect, useState, useCallback, useContext } from 'react';
import { useIntl } from 'react-intl-next';
import { Subscribe } from 'unstated';

import { useAnalyticsEvents } from '@atlaskit/analytics-next';
import type { PopupProps } from '@atlaskit/popup';
import { layers } from '@atlaskit/theme/constants';

import ByLine from '@atlassian/analytics-byline';

import { PageSegmentLoadStart, PageSegmentLoadEnd } from '@confluence/browser-metrics';
import { DialogsStateContainer } from '@confluence/dialogs';
import { Attribution, ErrorDisplay, withErrorBoundary } from '@confluence/error-boundary';
import {
	ExperienceStart,
	ExperienceTrackerContext,
	SUBMIT_GUEST_UPGRADE_FLOW_BY_ANALYTICS_EXPERIENCE,
} from '@confluence/experience-tracker';
import { GuestRequestToUpgradeByAnalytics } from '@confluence/external-collab-ui/entry-points/GuestRequestToUpgradeByAnalytics';
import { GuestRequestToUpgradeNotificationsWrapper } from '@confluence/external-collab-ui/entry-points/GuestRequestToUpgradeNotificationsWrapper';
import { useExternalCollabExperiment } from '@confluence/external-collab-ui/entry-points/useExternalCollabExperiment';
import { markErrorAsHandled } from '@confluence/graphql';
import { getMark } from '@confluence/performance';
import { withFlags } from '@confluence/flags';
import type { FlagsStateContainer } from '@confluence/flags';
import { useSSRPlaceholderReplaceIdProp } from '@confluence/loadable';
import { SSR_PARTIAL_COMPONENT } from '@confluence/ssr-utilities';
import { useChoreographerAssets } from '@confluence/choreographer-services';

import { useSSR, SSREvent } from '../../hooks';
import { CONTENT_ANALYTICS_VIEWERS_METRIC } from '../../perf.config';
import { AnalyticsDialogSsr } from '../AnalyticsDialog/AnalyticsDialogSsr';
import { isExpectedError } from '../utils';

import { SSR_EVENTS_CAPTURE_KEY, TestId } from './constants';
import { useAnalyticsByLineEntryPoint } from './useAnalyticsByLineEntryPoint';

export type AnalyticsByLineProps = {
	contentId: string;
	flags: FlagsStateContainer;
};

const AnalyticsByLineInternal: FC<AnalyticsByLineProps> = ({ contentId, flags }) => {
	const ssrPlaceholderIdProp = useSSRPlaceholderReplaceIdProp();
	const { formatMessage } = useIntl();
	const { onSSRCapturedEvent, wasComponentSSRed, wasSSREventTriggered } = useSSR({
		ssrEventCaptureKey: SSR_EVENTS_CAPTURE_KEY,
		name: SSR_PARTIAL_COMPONENT.AnalyticsByLine,
	});
	const [isPopupOpen, setPopupOpen] = useState(wasSSREventTriggered(SSREvent.CLICK));

	const { shouldRenderExperiment: shouldRenderGuestUpgradeExperiment } =
		useExternalCollabExperiment({
			flagKey: 'guest_upgrade_flow_analytics',
			source: 'analytics',
		});

	const [showGuestFlag, setShowGuestFlag] = useState<boolean>(false);

	const {
		error,
		loading,

		popupContent,
		popupContentExperience,
		onPopupContentClose,

		popupTrigger,
		popupTriggerExperience,
		onPopupTriggerSSREvent,
	} = useAnalyticsByLineEntryPoint({
		contentId,
		wasComponentSSRed,
	});

	useEffect(() => {
		if (wasComponentSSRed) {
			// https://hello.atlassian.net/l/cp/942Xd6LH#Instrumenting-your-feature
			CONTENT_ANALYTICS_VIEWERS_METRIC.markFMP(getMark('CFP-63.ssr-ttr'));
		}
	}, [wasComponentSSRed]);

	const experienceTracker = useContext(ExperienceTrackerContext);

	useEffect(() => {
		if (!wasComponentSSRed) {
			setPopupOpen(false);
		}
	}, [contentId, wasComponentSSRed]);

	const { createAnalyticsEvent } = useAnalyticsEvents();
	useEffect(() => {
		createAnalyticsEvent({
			type: 'sendTrackEvent',
			data: {
				action: 'displayed',
				actionSubject: 'analytics',
				actionSubjectId: contentId,
				objectId: contentId,
				source: 'confluence-frontend',
			},
		}).fire();
	}, [contentId, createAnalyticsEvent]);

	const onPopupClose: PopupProps['onClose'] = (...args) => {
		onPopupContentClose?.(...args);
		setPopupOpen(false);
	};

	const onPopupTriggerClick = useCallback(() => {
		onPopupTriggerSSREvent?.(SSREvent.CLICK);
		setPopupOpen(true);
		experienceTracker.start({ name: popupContentExperience });
	}, [experienceTracker, onPopupTriggerSSREvent, popupContentExperience]);
	useEffect(() => {
		onSSRCapturedEvent(SSREvent.CLICK, onPopupTriggerClick);
	}, [onSSRCapturedEvent, onPopupTriggerClick]);

	const onPopupTriggerFocus = useCallback(() => {
		onPopupTriggerSSREvent?.(SSREvent.FOCUS);
	}, [onPopupTriggerSSREvent]);
	useEffect(() => {
		onSSRCapturedEvent(SSREvent.FOCUS, onPopupTriggerFocus);
	}, [onSSRCapturedEvent, onPopupTriggerFocus]);

	const onPopupTriggerHover = useCallback(() => {
		onPopupTriggerSSREvent?.(SSREvent.HOVER);
	}, [onPopupTriggerSSREvent]);
	useEffect(() => {
		onSSRCapturedEvent(SSREvent.HOVER, onPopupTriggerHover);
	}, [onSSRCapturedEvent, onPopupTriggerHover]);

	useEffect(() => {
		if (error) {
			if (popupTriggerExperience) {
				experienceTracker.abort({
					name: popupTriggerExperience,
					reason: error.name,
				});
			}
			if (isExpectedError(error)) {
				markErrorAsHandled(error);
			}
		}
	}, [error, experienceTracker, popupTriggerExperience]);

	useEffect(() => {
		if (!loading && !error && popupTriggerExperience) {
			experienceTracker.succeed({ name: popupTriggerExperience });
		}
	}, [error, experienceTracker, loading, popupTriggerExperience]);

	const zIndex = layers.dialog();

	const { Popup } = useChoreographerAssets();

	const updateFlagState = useCallback((flagState: boolean) => {
		setShowGuestFlag(flagState);
	}, []);

	const onGuestInExperimentClick = useCallback(() => {
		createAnalyticsEvent({
			type: 'sendUIEvent',
			data: {
				action: 'clicked',
				actionSubject: 'AnalyticsButton',
				source: 'viewPageScreen',
				objectId: contentId,
				attributes: {
					from: 'fromByline',
					isGuest: true,
				},
			},
		}).fire();
		setShowGuestFlag(true);
	}, [contentId, createAnalyticsEvent]);

	// only guests are eligible for this experiment
	if (shouldRenderGuestUpgradeExperiment) {
		// for guests in this experiment, we only want it to say "Analytics" and not show the count
		// we don't want to show any popovers or hovers for guests to see information they shouldn't have access to
		// clicking on the Analytics button will trigger a flag to inform them that they don't have access to analytics and with options to request upgrade to user
		return (
			<Subscribe to={[DialogsStateContainer]}>
				{(dialogs: DialogsStateContainer) => (
					<GuestRequestToUpgradeNotificationsWrapper
						dialogs={dialogs}
						flags={flags}
						experienceTrackerName={SUBMIT_GUEST_UPGRADE_FLOW_BY_ANALYTICS_EXPERIENCE}
						flagActionSubjectId="guestAnalyticsRequestAccess"
						touchpoint="analyticsByLineButton"
						flagTitle={formatMessage(GuestRequestToUpgradeByAnalytics.flagTitle)}
						requestUpgradeFlagDescription={formatMessage(
							GuestRequestToUpgradeByAnalytics.requestUpgradeFlagDescription,
						)}
						requestPendingFlagDescription={formatMessage(
							GuestRequestToUpgradeByAnalytics.requestPendingFlagDescription,
						)}
						showFlag={showGuestFlag}
						updateFlagState={updateFlagState}
					>
						<ByLine
							count={0}
							isBylineComponent
							testId={TestId.BYLINE}
							onClick={onGuestInExperimentClick}
						/>
					</GuestRequestToUpgradeNotificationsWrapper>
				)}
			</Subscribe>
		);
	}

	return (
		<>
			{popupTriggerExperience ? <ExperienceStart name={popupTriggerExperience} /> : null}
			<Popup
				isOpen={isPopupOpen}
				onClose={onPopupClose}
				placement="bottom"
				trigger={(props) => {
					const { 'aria-expanded': ariaExpanded, ...triggerProps } = props;
					const trigger = popupTrigger({
						...props,
						onClick: onPopupTriggerClick,
					});

					return (
						// eslint-disable-next-line jsx-a11y/no-static-element-interactions
						<span
							{...triggerProps}
							onFocus={onPopupTriggerFocus}
							onMouseOver={onPopupTriggerHover}
							data-vc="analytics-byline-wrapper"
							{...ssrPlaceholderIdProp}
						>
							<PageSegmentLoadStart
								metric={CONTENT_ANALYTICS_VIEWERS_METRIC}
								// If we did SSR we want the start time to be the page start time.
								// If we did not SSR we want the start time to be now.
								isCustomStart={!wasComponentSSRed}
							/>
							{error && <ErrorDisplay error={error} />}
							{trigger && (
								<>
									{trigger}
									<PageSegmentLoadEnd metric={CONTENT_ANALYTICS_VIEWERS_METRIC} />
								</>
							)}
						</span>
					);
				}}
				content={(props) => popupContent({ ...props, zIndex })}
				zIndex={zIndex}
				messageId="confluence-analytics-analytics-by-line-popup"
				messageType="transactional"
			/>
			{process.env.REACT_SSR && <AnalyticsDialogSsr zIndex={zIndex} />}
		</>
	);
};

export const AnalyticsByLine = withErrorBoundary({
	attribution: Attribution.CC_ANALYTICS,
})(withFlags(AnalyticsByLineInternal));
